import { multipart } from '$/modules/graphql-client/multipart';

/**
 * Parses (possible deferred) graphql responses
 *
 * @see https://github.com/graphql/graphql-spec/pull/742/files
 */
export async function* graphql(
  response: Response,
): AsyncGenerator<GraphQLResponse> {
  const contentType = response.headers.get('content-type') ?? '';
  let chunks: Iterable<Response> | AsyncIterable<Response>;
  if (contentType === '' || contentType.includes('json')) {
    chunks = [response];
  } else if (contentType.startsWith('multipart/mixed')) {
    chunks = multipart(response);
  } else {
    throw new Error(`unknown content type: ${contentType}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any = null;
  const errors: GraphQLError[] = [];
  for await (const chunk of chunks) {
    // Revive 'null' as 'undefined' so optional chaining works consistently
    const part: MultipartResponse = JSON.parse(
      await chunk.text(),
      (key, value) => value ?? undefined,
    );
    if ('data' in part) data = part.data;
    if (part.errors) errors.push(...part.errors);
    if (part.incremental) {
      for (const increment of part.incremental) {
        let node = data;
        for (const name of increment.path) {
          let child = node[name];
          if (child == null) {
            // Create an empty node if the target doesn't exist
            child = node[name] = typeof name === 'string' ? {} : [];
          }
          node = child;
        }
        Object.assign(node, increment.data);
        if (increment.errors) {
          errors.push(...increment.errors);
        }
      }
    }
    yield { data, errors, hasNext: Boolean(part.hasNext) };
    if (!part.hasNext) return;
  }
}

interface GraphQLError {
  message?: string;
}

interface MultipartResponse {
  data?: unknown;
  incremental?: Array<DeferPayload>;
  errors?: GraphQLError[];
  hasNext: boolean;
}

type DeferPayload = {
  data: unknown;
  path: Array<string | number>;
  label?: string;
  errors?: GraphQLError[];
  extensions?: unknown[];
};

type GraphQLResponse = {
  data: unknown;
  errors: GraphQLError[];
  hasNext: boolean;
};
